import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map, tap } from 'rxjs';

import { ImpersonationActions } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { EffectsBase } from '@ps/core-data-access';
import { PlatformSwitcherService } from '@ps/platform-switcher-data-access';

import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from './survey-creation-core.actions';
import { selectSurvey } from './survey-creation-core.selectors';
import { surveyExitNavigation } from './utils/survey-route.helper';
import { specificAudienceReturnOf } from './utils/specific-audience-exit.helper';

// Reacts to a confirmed exit from the survey workflow — discard, save-and-exit,
// or save-and-exit-with-impersonation-change. Each effect owns exactly one
// outcome, split out of SurveyCreationSaveEffects so that class never has to
// know why a caller wanted to exit, only how to save. A plain exit lands on the
// survey's Manage Survey page once it is scheduled or launched (a Specific
// Audience channel on its parent's, with Traffic Channels open), else on the
// Dashboard; leaving the buyer
// context always lands on the Dashboard.
@Injectable()
export class SurveyCreationWorkflowExitEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _router = inject(Router);
  private readonly _store = inject(Store);
  private readonly _platformSwitcher = inject(PlatformSwitcherService);

  public readonly discardSurveyChanges$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreActions.discardSurveyChanges),
        concatLatestFrom(() => this._store.select(selectSurvey)),
        tap(([, survey]) =>
          this._router.navigate(
            ...surveyExitNavigation(
              survey,
              specificAudienceReturnOf(this._router),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  public readonly disconnectImpersonationOnSaveSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.saveSurveyAndDisconnectImpersonationSuccess,
      ),
      map(() => ImpersonationActions.disconnect()),
    ),
  );

  public readonly switchImpersonatedBuyerOnSaveSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess,
      ),
      map(({ buyer }) => ImpersonationActions.switchBuyer(buyer)),
    ),
  );

  public readonly switchToLegacyOnSaveSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreApiActions.saveSurveyAndSwitchToLegacySuccess),
        tap(({ routeKey, params }) =>
          this._platformSwitcher
            .switchToLegacy({ routeKey, params, force: true })
            .subscribe(),
        ),
      ),
    { dispatch: false },
  );

  public readonly navigateOnSaveAndExitSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreApiActions.saveSurveyAndExitSuccess),
        tap(({ survey }) =>
          this._router.navigate(
            ...surveyExitNavigation(
              survey,
              specificAudienceReturnOf(this._router),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  // Changing who is signed in leaves the survey behind, so these always go to
  // the dashboard.
  public readonly navigateToDashboardOnImpersonationExitSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          SurveyCreationCoreApiActions.saveSurveyAndDisconnectImpersonationSuccess,
          SurveyCreationCoreApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess,
        ),
        tap(() => this._router.navigate([APP_ROUTES.DASHBOARD])),
      ),
    { dispatch: false },
  );
}
